<template>
  <div ref="crudTableFitRoot">
    <v-data-table
      :headers="headers"
      :items="companies"
      class="elevation-1"
      hide-default-footer
      hide-details="auto"
      :loading="apiLoading"
      :loading-text="$t('table.loading')"
      :custom-sort="customSort"
      @update:options="changedOptions"
    >
      <template slot="top">
        <v-toolbar flat class="solvit-crud-table-toolbar">
          <v-toolbar-title style="align-items: center; display: flex">
            <v-icon left>
              mdi-domain
            </v-icon>{{ $t('companyCrud.title') }}
          </v-toolbar-title>
          <v-divider class="mx-4" inset vertical />
          <v-spacer />
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('companyCrud.searchLabel')"
            single-line
            hide-details="auto"
            @input="handleSearch"
          />
          <v-spacer />
          <v-btn
            outlined
            style="font-weight: 400"
            class="mb-2 solvit-crud-outline-btn solvit-crud-outline-btn--primary"
            @click="openAddDialog"
          >
            <v-icon size="medium">
              mdi-plus
            </v-icon>
            {{ $t('common.addNew') }}
          </v-btn>
        </v-toolbar>
      </template>
      <template slot="item.actions" slot-scope="{ item }">
        <FixDeleteBtn
          :key="item.id"
          :item="item"
          @editItem="editItem"
          @deleteItem="deleteItem"
        />
      </template>
      <template slot="footer">
        <v-pagination
          v-model="page"
          :length="totalPages"
          :total-visible="10"
          next-icon="mdi-chevron-right"
          prev-icon="mdi-chevron-left"
        />
      </template>
    </v-data-table>

    <v-dialog
      :key="dialogKey"
      v-model="dialog"
      :max-width="modalLayout.MD"
      v-bind="modalP2FormViewport"
      @keydown.esc="close"
    >
      <v-card>
        <CrudFormDialogHeader
          :kicker="$t('companyCrud.title')"
          :title="formTitle"
          show-close
          @close="close"
        />

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.companyName"
                  :label="$t('companyCrud.fieldCompanyName')"
                  :rules="[requiredRule]"
                  required
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn outlined class="solvit-crud-outline-btn" @click="close">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn outlined class="solvit-crud-outline-btn solvit-crud-outline-btn--primary" @click="save">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FixDeleteBtn from '../button/FixDeleteBtn.vue';
import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
import { mapGetters, mapActions } from 'vuex';
import { getSortParam } from '../../utils/sortUtils';
import { MODAL_MAX_WIDTH, MODAL_P2_FORM_VIEWPORT } from '@/constants/modalLayout';
import { crudTablePageSizeFitMixin } from '@/ui/mixins/crudTablePageSizeFitMixin';

export default {
  name: 'CompanyCrudTable',
  mixins: [crudTablePageSizeFitMixin],
  components: {
    FixDeleteBtn,
    CrudFormDialogHeader
  },
  data: () => ({
    modalLayout: MODAL_MAX_WIDTH,
    modalP2FormViewport: MODAL_P2_FORM_VIEWPORT,
    dialog: false,
    dialogKey: 0,
    valid: true,
    editedIndex: -1,
    editedItem: {
      id: null,
      companyName: ''
    },
    defaultItem: {
      id: null,
      companyName: ''
    },
    search: '',
    page: 1,
    itemsPerPage: 10,
    totalItems: 0,
    sortBy: ['id'],
    sortDesc: [true],
    pageCount: 0,
    paging: true,
    loading: false
  }),
  computed: {
    ...mapGetters({
      companies: 'company/getCompanies',
      totalElements: 'company/getTotalElements',
      totalPages: 'company/getTotalPages',
      apiLoading: 'company/isLoading',
      error: 'company/getError'
    }),
    formTitle() {
      return this.editedIndex === -1
        ? this.$t('companyCrud.addCompany')
        : this.$t('companyCrud.editCompany');
    },
    requiredRule() {
      return (v) => !!v || this.$t('common.required');
    },
    headers() {
      void this.$i18n.locale;
      return [
        { text: this.$t('companyCrud.colCompanyName'), value: 'companyName', sortable: true },
        {
          text: this.$t('companyCrud.colActions'),
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '120px'
        }
      ];
    }
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    page(val) {
      if (this.isCrudTableFitProgrammaticUpdate()) {
        return;
      }
      this.searchCompanyData(this.search, val, this.sortBy, this.sortDesc);
    }
  },
  created() {
    //changedOptions에서 실행함
    // this.initialize();
  },
  methods: {
    ...mapActions({
      fetchCompanies: 'company/fetchCompanies',
      createCompany: 'company/createCompany',
      updateCompany: 'company/updateCompany',
      deleteCompany: 'company/deleteCompany'
    }),
    onCrudTableRequestCancel() {
      this.$store.dispatch('company/cancelRequests');
    },
    async refetch(){
      await this.fetchCompanies({
        page: this.page - 1,
        size: this.itemsPerPage,
        sort: `${this.sortBy[0]},${this.sortDesc[0] ? 'DESC' : 'ASC'}`,
        keyword: this.search
      });
    },
    async initialize() {
      await this.fetchCompanies({
        page: this.page - 1,
        size: this.itemsPerPage,
        sort: `${this.sortBy[0]},${this.sortDesc[0] ? 'DESC' : 'ASC'}`,
        keyword: this.search
      });

      this.totalItems = this.pageCount * this.itemsPerPage;
    },
    async handleSearch() {
      this.page = 1;
      await this.initialize();
    },
    async handlePageChange(newPage) {
      this.page = newPage;
      await this.initialize();
    },
    /**
     * 기존의 sort기능을 사용하지 않도록 처리
     */
    customSort(items, _index, _isDesc) {
      return items;
    },
    changeSortByArrayToApiParam(sortBy) {
      if (sortBy && sortBy?.length > 0) {
        if (sortBy[0] === 'companyName') return ['companyName'];
        else if (sortBy[0] === 'companyCode') return ['companyCode'];
        else if (sortBy[0] === 'companyStatus') return ['companyStatus'];
        else return ['id'];
      }
      return ['id'];
    },
    changedOptions(val) {
      /**
       * groupBy : Array, groupDesc : Array, itemsPerPage : Number, multiSort : Boolean, mustSort : Boolean, page : Number, sortBy : Array, sortDesc : Array
       */
      const tempPage = this.page;
      if (val?.sortBy?.length > 0) {
        this.sortBy = val.sortBy;
      } else {
        this.sortBy = ['id'];
      }
      if (val?.sortDesc?.length > 0) {
        this.sortDesc = val.sortDesc;
      }
      this.searchCompanyData(this.search, tempPage, this.sortBy, this.sortDesc);
    },
    async searchCompanyData(keyword, page, sortBy, sortDesc) {
      try {
        const tempSortBy = this.changeSortByArrayToApiParam(sortBy);
        await this.fetchCompanies({
          page: page-1,
          size: this.itemsPerPage,
          sort: tempSortBy ? `${tempSortBy[0]},${getSortParam(sortDesc?.[0])}` : null,
          keyword: keyword
        });
      } catch (error) {
        console.error('Error searching companies:', error);
        alert(this.$t('companyCrud.searchError'));
      }
    },
    openAddDialog() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.dialog = true;
    },
    editItem(item) {
      this.editedIndex = this.companies.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    async deleteItem(item) {
      if (confirm(this.$t('companyCrud.deleteConfirm'))) {
        try {
          await this.deleteCompany(item.id);
          this.refetch();
        } catch (error) {
          console.error('Error deleting company:', error);
        }
      }
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        this.dialogKey++;
      });
    },
    async save() {
      try {
        if (this.editedIndex > -1) {
          await this.updateCompany(this.editedItem);
        } else {
          await this.createCompany(this.editedItem);
        }
        this.close();
        await this.refetch();
      } catch (error) {
        console.error('Error saving company:', error);
        alert(this.$t('companyCrud.saveError'));
      }
    }
  }
};
</script>

<style scoped>
.custom-padding {
  padding: 8px 12px !important;
}
</style>
